import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, Clock } from "lucide-react";
import { useLanguage } from "../i18n/translations";
import { BUSINESS } from "../constants/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", service: "", monthly_bill: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let saved = false;
    try {
      await axios.post(`${API}/leads`, form);
      saved = true;
      toast.success(t.form.success);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save enquiry, opening WhatsApp anyway.");
    }
    const msg = `Hello Uma Enterprises!%0A%0AName: ${encodeURIComponent(form.name)}%0APhone: ${encodeURIComponent(form.phone)}%0AEmail: ${encodeURIComponent(form.email || "-")}%0ACity/Pincode: ${encodeURIComponent(form.city || "-")}%0AService: ${encodeURIComponent(form.service)}%0AMonthly Bill: ${encodeURIComponent(form.monthly_bill || "-")}%0A%0APlease contact me with a quote.`;
    window.open(`https://wa.me/918922004004?text=${msg}`, "_blank");
    setLoading(false);
    if (saved) setForm({ name: "", phone: "", email: "", city: "", service: "", monthly_bill: "" });
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-navy-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-solar/60 focus:ring-2 focus:ring-solar/20";

  return (
    <section id="contact" className="relative overflow-hidden py-20 lg:py-28">
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-[400px] w-[600px] rounded-full bg-eco/8 blur-[130px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
        >
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-solar-highlight">{t.form.eyebrow}</p>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">{t.form.title}</h2>
          <p className="mt-4 max-w-md leading-relaxed text-slate-400">{t.form.desc}</p>

          <div className="mt-10 space-y-5">
            <a href="tel:+918922004004" className="flex items-center gap-4 text-slate-200 transition-colors hover:text-solar-yellow" data-testid="contact-phone-link">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-navy-900"><Phone size={18} className="text-solar-yellow" /></span>
              <span className="font-mono text-lg font-bold">+91 89220 04004</span>
            </a>
            <a href="mailto:enterprises.uma08@gmail.com" className="flex items-center gap-4 text-slate-200 transition-colors hover:text-solar-yellow" data-testid="contact-email-link">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-navy-900"><Mail size={18} className="text-solar-yellow" /></span>
              <span className="text-sm font-medium sm:text-base">enterprises.uma08@gmail.com</span>
            </a>
            <div className="flex items-center gap-4 text-slate-200">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-navy-900"><MapPin size={18} className="text-solar-yellow" /></span>
              <span className="text-sm font-medium sm:text-base">{t.visit.address}</span>
            </div>
            <div className="flex items-center gap-4 text-slate-400">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-navy-900"><Clock size={18} className="text-solar-yellow" /></span>
              <span className="text-sm font-medium">{t.visit.hours}</span>
            </div>
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 shadow-xl" data-testid="visit-map-card">
            <iframe
              title="Uma Enterprises location map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.mapQuery)}&output=embed`}
              className="h-60 w-full"
              loading="lazy"
              data-testid="visit-map"
            />
            <div className="flex items-center justify-between bg-navy-900 px-4 py-3">
              <span className="text-xs text-slate-400">{t.visit.address}</span>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS.mapQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold text-solar-highlight transition-colors hover:text-solar-yellow"
                data-testid="visit-directions-link"
              >
                {t.visit.directions} →
              </a>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, delay: 0.1 }}
          className="glass-card rounded-3xl p-7 sm:p-9"
          data-testid="lead-form"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.form.name} *</label>
              <input required maxLength={120} value={form.name} onChange={set("name")} className={inputCls} placeholder="Ramesh Kumar" data-testid="lead-input-name" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.form.phone} *</label>
              <input required type="tel" maxLength={20} pattern={"[0-9+\\- ]{10,20}"} value={form.phone} onChange={set("phone")} className={inputCls} placeholder="98765 43210" data-testid="lead-input-phone" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.form.email}</label>
              <input type="email" maxLength={254} value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="lead-input-email" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.form.city}</label>
              <input maxLength={120} value={form.city} onChange={set("city")} className={inputCls} placeholder="Neda / 201301" data-testid="lead-input-city" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.form.service} *</label>
              <select required value={form.service} onChange={set("service")} className={inputCls} data-testid="lead-input-service">
                <option value="" disabled>—</option>
                {t.form.options.map((o) => <option key={o} value={o} className="bg-navy-950">{o}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.form.bill}</label>
              <select value={form.monthly_bill} onChange={set("monthly_bill")} className={inputCls} data-testid="lead-input-bill">
                <option value="" disabled>—</option>
                {t.form.bills.map((o) => <option key={o} value={o} className="bg-navy-950">{o}</option>)}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="lead-form-submit-btn"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-eco px-6 py-4 text-sm font-bold text-amber-950 transition-colors duration-300 hover:bg-eco-bright disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {t.form.submit}
          </button>
          <p className="mt-3 text-center text-xs text-slate-500">{t.form.note}</p>
        </motion.form>
      </div>
    </section>
  );
};
